import React, { useRef } from 'react';
import { useQuery } from 'react-query';
import { QRCodeSVG } from 'qrcode.react';
import { 
  BuildingStorefrontIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  MapPinIcon,
  ShareIcon,
  PrinterIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const StoreCard = () => {
  const cardRef = useRef(null);

  const { data: profile, isLoading } = useQuery(
    'vendor-profile',
    () => api.get('/vendors/profile').then(res => res.data.data)
  );

  const storeUrl = profile ? `${window.location.origin}/vendors/store/${profile.store_slug}` : '';

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Convert card to image and download
    toast.success('جاري تحميل البطاقة...');
    // You can implement html2canvas here for better quality
    window.print();
  };

  const handleShare = (platform) => {
    const text = `تفضل بزيارة متجر ${profile?.store_name} على أمازول`;
    const url = storeUrl;

    const shareUrls = {
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(storeUrl);
    toast.success('تم نسخ الرابط');
  };

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">بطاقة المتجر</h1>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <PrinterIcon className="w-5 h-5" />
              طباعة
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              تحميل
            </button>
          </div>
        </div>

        {/* Store Card */}
        <div className="max-w-4xl mx-auto">
          <div 
            ref={cardRef}
            className="bg-white rounded-2xl shadow-xl overflow-hidden print:shadow-none"
          >
            {/* Card Header with Banner */}
            <div className="relative h-48 bg-gradient-to-br from-primary-500 to-primary-700">
              {profile?.banner_url ? (
                <img
                  src={profile.banner_url}
                  alt="Store Banner"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700" />
              )}
              
              {/* Logo */}
              <div className="absolute -bottom-16 right-8">
                {profile?.logo_url ? (
                  <img
                    src={profile.logo_url}
                    alt={profile.store_name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center">
                    <BuildingStorefrontIcon className="w-16 h-16 text-primary-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Card Content */}
            <div className="p-8 pt-20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Store Info */}
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {profile?.store_name}
                    </h2>
                    {profile?.description && (
                      <p className="text-gray-600">
                        {profile.description}
                      </p>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-3">
                    {profile?.user?.phone && (
                      <div className="flex items-center gap-3 text-gray-700">
                        <PhoneIcon className="w-5 h-5 text-primary-600" />
                        <span className="font-medium">{profile.user.phone}</span>
                      </div>
                    )}
                    {profile?.user?.email && (
                      <div className="flex items-center gap-3 text-gray-700">
                        <EnvelopeIcon className="w-5 h-5 text-primary-600" />
                        <span className="font-medium">{profile.user.email}</span>
                      </div>
                    )}
                    {profile?.address && (
                      <div className="flex items-center gap-3 text-gray-700">
                        <MapPinIcon className="w-5 h-5 text-primary-600" />
                        <span className="font-medium">{profile.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex gap-6 pt-4 border-t border-gray-200">
                    <div>
                      <p className="text-sm text-gray-600">التقييم</p>
                      <p className="text-2xl font-bold text-primary-600">
                        {parseFloat(profile?.rating_average || 0).toFixed(1)} ⭐
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">عدد التقييمات</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {profile?.rating_count || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="bg-white p-4 rounded-lg border-4 border-primary-600 shadow-lg">
                    <QRCodeSVG
                      value={storeUrl}
                      size={180}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <p className="text-sm text-gray-600 text-center">
                    امسح الكود للوصول للمتجر
                  </p>
                </div>
              </div>

              {/* Store URL */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <code className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {storeUrl}
                  </code>
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 whitespace-nowrap"
                  >
                    نسخ الرابط
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="mt-6 bg-white rounded-lg shadow p-6 print:hidden">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ShareIcon className="w-5 h-5" />
              مشاركة المتجر
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <button
                onClick={() => handleShare('whatsapp')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                واتساب
              </button>

              <button
                onClick={() => handleShare('facebook')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                فيسبوك
              </button>

              <button
                onClick={() => handleShare('twitter')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                تويتر
              </button>

              <button
                onClick={() => handleShare('telegram')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                تيليجرام
              </button>

              <button
                onClick={() => handleShare('linkedin')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                لينكد إن
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:shadow-none,
          .print\\:shadow-none * {
            visibility: visible;
          }
          .print\\:shadow-none {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </VendorLayout>
  );
};

export default StoreCard;
